import { useState } from 'react';
import { CheckCircle, ChevronLeft, ChevronRight, MapPin, NotebookText, Upload, Users, X, XCircle, Trash2, Edit, Info } from 'lucide-react';
import { Loader } from './ui/Loader';
import { LinkifiedText } from './ui/LinkifiedText';
import { motion, AnimatePresence } from 'motion/react';
import { FullscreenShell } from './layout/FullscreenShell';
import { useModalFocus } from '../utils/hooks/useModalFocus';

interface TalentPackageDetailModalProps {
  packageItem: any;
  hasRequested: boolean;
  isOwnPackage: boolean;
  onClose: () => void;
  onSendRequest?: (packageId: string) => void;
  onEdit?: (packageId: string) => void;
  onClosePackage?: (packageId: string) => void;
  onPublishPackage?: (packageId: string) => void;
  onDeletePackage?: (packageId: string) => void;
  onViewRequests?: (packageId: string) => void;
}

function formatPriceRange(priceMin?: number | null, priceMax?: number | null, currency?: string | null): string {
  const safeCurrency = currency || 'NOK';
  if (typeof priceMin === 'number' && typeof priceMax === 'number') return `${priceMin} - ${priceMax} ${safeCurrency}`;
  if (typeof priceMin === 'number') return `Fra ${priceMin} ${safeCurrency}`;
  if (typeof priceMax === 'number') return `Inntil ${priceMax} ${safeCurrency}`;
  return 'Pris etter avtale';
}

export function TalentPackageDetailModal({
  packageItem,
  hasRequested,
  isOwnPackage,
  onClose,
  onSendRequest,
  onEdit,
  onClosePackage,
  onPublishPackage,
  onDeletePackage,
  onViewRequests,
}: TalentPackageDetailModalProps) {
  useModalFocus(true, { fullscreen: true });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isImageFullscreen, setIsImageFullscreen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const coverImageUrl = packageItem?.coverImageUrl ?? packageItem?.cover_image_url;
  const imageUrls = packageItem?.imageUrls ?? packageItem?.image_urls;
  const imageUrl = coverImageUrl ?? (Array.isArray(imageUrls) && imageUrls.length > 0 ? imageUrls[0] : packageItem?.imageUrl ?? packageItem?.image_url ?? packageItem?.image);
  const allImages = [imageUrl].filter(Boolean).concat(imageUrls || []);
  const hasMultipleImages = allImages.length > 1;

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  const title = packageItem?.title || 'Tjeneste';
  const description = packageItem?.description || '';
  const status = packageItem?.status || 'active';

  return (
    <>
    <FullscreenShell title="Tjenestedetaljer" onClose={onClose} onBack={onClose} contentClassName="space-y-6">
      <div className="space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-purple-900">{title}</h1>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-100 text-purple-700">
              <NotebookText className="w-3.5 h-3.5" />
              {formatPriceRange(
                packageItem?.priceMin ?? packageItem?.price_min,
                packageItem?.priceMax ?? packageItem?.price_max,
                packageItem?.currency
              )}
            </span>
            {status !== 'active' && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gray-100 text-gray-700">
                <XCircle className="w-3.5 h-3.5" />
                {status === 'closed' ? 'Avpublisert' : 'Utkast'}
              </span>
            )}
          </div>
        </div>

        {imageUrl && (
          <div className="w-full rounded-xl overflow-hidden border border-purple-100">
            <img src={imageUrl} alt={title} className="w-full rounded-xl object-cover" />
          </div>
        )}
        {Array.isArray(imageUrls) && imageUrls.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-2">
            {imageUrls.slice(0, 5).map((url: string, idx: number) => (
              <button
                key={idx}
                type="button"
                onClick={() => { setCurrentImageIndex(imageUrl ? 1 + idx : idx); setIsImageFullscreen(true); }}
                className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden border border-purple-100 cursor-pointer"
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {packageItem?.county && (
          <div className="inline-flex items-center gap-2 text-gray-700">
            <MapPin className="w-4 h-4" />
            <span>{packageItem.county}</span>
          </div>
        )}

        <div>
          <h3 className="font-semibold text-purple-900 mb-2">Beskrivelse</h3>
          <LinkifiedText text={description} className="text-gray-700" />
        </div>
      </div>

      <div className="border-t border-gray-200 pt-4 space-y-3 xl:pb-8">
        {isOwnPackage ? (
          <>
            {onEdit && status === 'active' && (
              <button
                onClick={() => onEdit(packageItem.id)}
                className="w-full px-5 py-3 rounded-lg bg-blue-100 text-blue-700 font-semibold hover:bg-blue-200 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Rediger tjeneste
              </button>
            )}
            {onClosePackage && status === 'active' && (
              <button
                onClick={() => onClosePackage(packageItem.id)}
                className="w-full px-5 py-3 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors inline-flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                Avpubliser
              </button>
            )}
            {onPublishPackage && status !== 'active' && (
              <button
                onClick={() => onPublishPackage(packageItem.id)}
                className="w-full px-5 py-3 rounded-lg bg-green-100 text-green-700 font-semibold hover:bg-green-200 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Publiser igjen
              </button>
            )}
            {onViewRequests && (
              <button
                onClick={() => onViewRequests(packageItem.id)}
                className="w-full px-5 py-3 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Users className="w-4 h-4" />
                Forespørsler
              </button>
            )}
            {onDeletePackage && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="w-full px-5 py-3 rounded-lg bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Slett tjeneste
              </button>
            )}
          </>
        ) : hasRequested ? (
          <button
            disabled
            className="w-full px-5 py-3 rounded-lg bg-green-100 text-green-700 font-semibold cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            Forespørsel sendt
          </button>
        ) : (
          <button
            onClick={() => onSendRequest?.(packageItem.id)}
            className="w-full px-5 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold hover:shadow-lg transition-all"
          >
            Send forespørsel
          </button>
        )}
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Slett tjeneste?</h3>
            <p className="text-sm text-gray-600 mb-5">Denne handlingen kan ikke angres.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  onDeletePackage?.(packageItem.id);
                }}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700"
              >
                Slett
              </button>
            </div>
          </div>
        </div>
      )}
    </FullscreenShell>

      {/* Fullscreen Image Viewer */}
      {isImageFullscreen && allImages.length > 0 && (
        <div
          className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md z-[60] flex items-center justify-center p-4 pt-page-safe pb-safe"
          onClick={() => setIsImageFullscreen(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setIsImageFullscreen(false); }}
            className="absolute top-safe-offset right-4 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all z-30"
            aria-label="Lukk fullskjerm"
          >
            <X className="w-6 h-6 text-gray-700" />
          </button>
          <div className="flex flex-col items-center max-w-full max-h-full">
            <img
              src={allImages[currentImageIndex]}
              alt={`${title} - Fullskjerm`}
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
            />
            <p className="text-center text-white/90 text-lg mt-4">
              {currentImageIndex + 1} / {allImages.length}
            </p>
          </div>
          {hasMultipleImages && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-2 xl:left-4 top-1/2 -translate-y-1/2 p-2 xl:p-4 bg-white/40 xl:bg-white xl:bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
              >
                <ChevronLeft className="w-5 h-5 xl:w-8 xl:h-8 text-gray-800" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-2 xl:right-4 top-1/2 -translate-y-1/2 p-2 xl:p-4 bg-white/40 xl:bg-white xl:bg-opacity-90 rounded-full shadow-lg hover:bg-opacity-100 transition-all"
              >
                <ChevronRight className="w-5 h-5 xl:w-8 xl:h-8 text-gray-800" />
              </button>
            </>
          )}
        </div>
      )}
    </>
  );
}
